import React, { useState, useEffect, useMemo } from 'react';
import { PrismButton, PrismSelect, PrismBanner, PrismLoading, PrismCard, PrismBadge } from '@prism-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import ApplicationTimeline from './ApplicationTimeline.jsx';
import { loadChapterStepData, peekChapterStepData } from './chapterStepData';

const ChapterStep = ({
  currentUser,
  application,
  saveApplication,
  updateApplicationState,
  jumpToStep,
}) => {
  var thisPageStep = 1;

  // Seed everything synchronously so the first paint is the finished step rather than a
  // spinner followed by the select, then the timeline card a frame later.
  const [data, setData] = useState(() => peekChapterStepData());
  const [selectedChapterId, setSelectedChapterId] = useState(() => {
    const initial = application?.chapter_id || currentUser?.chapter_id;
    return initial ? String(initial) : '';
  });
  const [error, setError] = useState(null);

  const loading = !data;
  const chapters = data?.chapters ?? [];
  const applicationWindows = data?.applicationWindows ?? [];
  const chapterCohorts = data?.chapterCohorts ?? [];

  const fetchData = async () => {
    try {
      setError(null);
      setData(await loadChapterStepData());
    } catch (err) {
      console.error('Error in fetchData:', err);
      setError('Failed to load chapter data. Please try again later.');
    }
  };

  // Always revalidate; with a prefetched result this happens behind an already-painted step.
  useEffect(() => {
    fetchData();
  }, []);

  // Adopt a chapter that arrives after mount, without overriding the applicant's own pick.
  useEffect(() => {
    if (!selectedChapterId) {
      const initial = application?.chapter_id || currentUser?.chapter_id;
      if (initial) setSelectedChapterId(String(initial));
    }
  }, [application?.chapter_id, currentUser?.chapter_id, selectedChapterId]);

  const selectedChapterWindow = useMemo(
    () =>
      (selectedChapterId &&
        applicationWindows.find((w) => w.chapter_id === parseInt(selectedChapterId))) ||
      null,
    [selectedChapterId, applicationWindows]
  );
  const selectedChapterCohort = useMemo(
    () =>
      (selectedChapterId &&
        chapterCohorts.find((c) => c.chapter_id === parseInt(selectedChapterId))) ||
      null,
    [selectedChapterId, chapterCohorts]
  );

  const onBack = async () => {
    jumpToStep(thisPageStep - 1);
  };

  // Check if application window is currently open
  const isApplicationWindowOpen = (window) => {
    if (!window || !window.opening || !window.hard_deadline) return false;
    
    const now = new Date();
    const opening = new Date(window.opening);
    const hard_deadline = new Date(window.hard_deadline);
    
    return now >= opening && now <= hard_deadline;
  };

  // Get window status for display
  const getWindowStatus = (window) => {
    if (!window || !window.opening || !window.hard_deadline) return 'No information available';
    
    const now = new Date();
    const opening = new Date(window.opening);
    const hard_deadline = new Date(window.hard_deadline);

    if (now < opening) {
      return 'Opens Soon';
    } else if (now > hard_deadline) {
      return 'Closed';
    } else {
      return 'Open Now';
    }
  };



  const handleChapterChange = (e) => {
    const chapterId = e.target.value;
    setSelectedChapterId(chapterId);

    // Update application state only (no database save)
    if (updateApplicationState && chapterId) {
      updateApplicationState({ chapter_id: parseInt(chapterId) });
      console.log('Chapter ID updated in application state:', parseInt(chapterId));
    }
  };

  const handleContinue = () => {
    if (!selectedChapterId) {
      alert('Please select a chapter before continuing.');
      return;
    }

    // Check if application window is open
    if (!isApplicationWindowOpen(selectedChapterWindow)) {
      alert('The application window for this chapter is not currently open.');
      return;
    }

    // Only save if we have an existing application with all required fields
    if (saveApplication && selectedChapterId && application?.id) {
      try {
        saveApplication({
          ...application,
          chapter_id: parseInt(selectedChapterId),
        });
      } catch (saveError) {
        console.error('Error saving application:', saveError);
        // Don't prevent navigation if save fails
      }
    }

    // Always continue to next step
    if (jumpToStep) {
      jumpToStep(2); // Go to step 2 (Role step)
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';

      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      }).format(date);
    } catch (err) {
      return 'Invalid date';
    }
  };

  const getChapterName = (chapter) => {
    if (!chapter || !chapter.location) return 'Unknown Chapter';
    return chapter.location;
  };

  // Check if we should show the continue button
  const shouldShowContinueButton =
    selectedChapterId && isApplicationWindowOpen(selectedChapterWindow);

  if (loading) {
    return (
      <div style={{ textAlign: 'center' }}>
        <PrismLoading as="spinner" container="inline" />
        <p style={{ marginTop: '0.5rem' }}>Loading chapters...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PrismBanner tone="danger" layout="inline">
          <strong>Error Loading Data:</strong> {error}
        </PrismBanner>
        <div style={{ marginTop: '1rem' }}>
          <PrismButton
            tone="danger"
            display="outline"
            onPrismClick={() => fetchData()}
          >
            Retry
          </PrismButton>
        </div>
      </div>
    );
  }
  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h3 style={{ marginBottom: '0.5rem', textAlign: 'center' }}>Select Your Chapter</h3>
      <p style={{ marginBottom: '1.5rem', color: 'var(--color-text-faded)', textAlign: 'center' }}>
        Each chapter has its own application window and deadlines.
      </p>

      <PrismCard display="static" style={{ marginBottom: '1.5rem', maxWidth: '400px', margin: '0 auto 1.5rem', '--prism-font-caption-base-size': '1.25rem' }}>
        <PrismSelect
          label="Chapter"
          name="chapter"
          value={selectedChapterId}
          options={[
            { value: '', display: '-- Select a Chapter --' },
            ...(chapters && chapters.length > 0
              ? chapters.map((chapter) => ({
                  value: String(chapter.id),
                  display: getChapterName(chapter),
                }))
              : []),
          ]}
          onPrismChange={(e) => {
            const val = e.detail?.value ?? e.detail;
            handleChapterChange({ target: { value: String(val) } });
          }}
        />
      </PrismCard>

          {/* Application Window Information */}
          {selectedChapterWindow && (
            <PrismCard display="static" style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h5 style={{ margin: 0 }}>Application Timeline</h5>
                <PrismBadge tone={isApplicationWindowOpen(selectedChapterWindow) ? 'success' : 'danger'}>
                  {getWindowStatus(selectedChapterWindow)}
                </PrismBadge>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-faded)', marginBottom: '0.25rem' }}>Opens</div>
                  <div style={{ fontWeight: 600 }}>{formatDate(selectedChapterWindow.opening)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-faded)', marginBottom: '0.25rem' }}>Closes</div>
                  <div style={{ fontWeight: 600 }}>{formatDate(selectedChapterWindow.soft_deadline)}</div>
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: '0.75rem 0' }} />

              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-faded)', marginBottom: '0.25rem' }}>Results Communicated By</div>
                <div style={{ fontWeight: 600 }}>{formatDate(selectedChapterWindow.notification_date)}</div>
              </div>

              {selectedChapterCohort && (
                <>
                  <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: '0.75rem 0' }} />
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-faded)', marginBottom: '0.25rem' }}>Mentee Seats Available</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{selectedChapterCohort.mentees_count}</div>
                  </div>
                </>
              )}
            </PrismCard>
          )}

          {/* Application Window Closed Warning */}
          {selectedChapterId && selectedChapterWindow && !isApplicationWindowOpen(selectedChapterWindow) && (
            <PrismBanner tone="caution" layout="inline" style={{ marginBottom: '1rem' }}>
              <strong>Application Window Closed:</strong> The application window for this chapter is not currently open. 
              Applications are accepted from {formatDate(selectedChapterWindow.opening)} to {formatDate(selectedChapterWindow.soft_deadline)}.
            </PrismBanner>
          )}

          {/* No Application Window Warning */}
          {selectedChapterId &&
            !selectedChapterWindow &&
            applicationWindows.length > 0 && (
              <PrismBanner tone="caution" layout="inline" style={{ marginBottom: '1rem' }}>
                <strong>Notice:</strong> No application window information is
                available for the selected chapter. Please contact the chapter
                directly for application deadlines.
              </PrismBanner>
            )}

          {/* No Data Available */}
          {selectedChapterId && applicationWindows.length === 0 && (
            <PrismBanner tone="info" layout="inline" style={{ marginBottom: '1rem' }}>
              <strong>Information:</strong> Application window data is not
              currently available. Please proceed with your application and
              check back later for deadline information.
            </PrismBanner>
          )}

          {/* Navigation Buttons */}
          <div style={{ marginTop: '2.5rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: shouldShowContinueButton
                  ? 'space-between'
                  : 'flex-start',
                alignItems: 'center',
                width: '100%',
              }}
            >
              {/* Back Button - Always show */}
              <PrismButton
                tone="primary-brand"
                size="lg"
                onPrismClick={onBack}
              >
                <FontAwesomeIcon icon={faChevronLeft} /> Back
              </PrismButton>

              {/* Continue Button - Only show when application window is open */}
              {shouldShowContinueButton && (
                <PrismButton
                  tone="primary-brand"
                  size="lg"
                  onPrismClick={handleContinue}
                >
                  Continue <FontAwesomeIcon icon={faChevronRight} />
                </PrismButton>
              )}
            </div>

            {/* Error/Status messages centered below buttons */}
            {!selectedChapterId && (
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <small style={{ color: 'var(--color-text-faded)' }}>
                  Please select a chapter to continue
                </small>
              </div>
            )}

            {selectedChapterId &&
              !shouldShowContinueButton &&
              selectedChapterWindow && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <small style={{ color: 'var(--color-text-faded)' }}>
                    Application window is not currently open for this chapter
                  </small>
                </div>
              )}
          </div>
    </div>
  );
};

export default ChapterStep;
